import { MailIcon, PhoneIcon, PlayIcon } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { BsTwitterX } from 'react-icons/bs';
import { FaInstagram, FaStreetView, FaTelegram, FaYoutube } from 'react-icons/fa';
import { FaLocationPin, FaTiktok, FaWhatsapp } from 'react-icons/fa6';
import { FcPrivacy } from 'react-icons/fc';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

const Section = ({ id, number, title, children, icon }) => {
  return (
    <section
      id={id}
      className="scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.025] overflow-hidden"
    >
      <div className="flex items-center gap-4 px-6 py-5 border-b border-white/10 bg-white/[0.02]">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-400/20 flex items-center justify-center">
          {icon || (
            <span className="text-blue-400 font-semibold">
              {number}
            </span>
          )}
        </div>

        <div>
          {number && (
            <div className="text-[11px] uppercase tracking-[0.2em] text-blue-400 mb-1">
              Section {number}
            </div>
          )}

          <h2 className="text-xl sm:text-2xl font-bold text-[#3E3274]">
            {title}
          </h2>
        </div>
      </div>

      <div className="px-6 sm:px-8 py-7 text-sm sm:text-[15px] leading-7 text-gray-400">
        {children}
      </div>
    </section>
  );
};

const Icon = ({ children }) => (
  <svg
    className="w-5 h-5 text-blue-400"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth="1.8"
  >
    {children}
  </svg>
);


export default function PrivacyPolicy() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [activeDocument, setActiveDocument] = useState("terms");

  const handleDocumentChange = (section) => {
    setActiveDocument(section);

    const element = document.getElementById(section);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const fetchSectionHero = async () => {      
    try {
        const res = await fetch(
            `${process.env.REACT_APP_BASE_URL}api/sectionhero/privacy`
        );

        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const json = await res.json();
        setSectionHero(json.data[0] || []);

    } catch (err) {
        console.error("Fetch Pc error:", err);
    }
  };
  
  useEffect(() => {
    fetchSectionHero();
  }, []);  

  const [sectionHero, setSectionHero] = useState({
    title: "",
    page: "",
    description: "",
    image: "", 
  });

  const clickMaps = () => {
    // Perform any logic here before navigating
    window.open("https://maps.app.goo.gl/orU8WGav3zU4Ma2A6", "_blank")
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.75 && rect.bottom > 0;
        
        if (isVisible) {
          setVisibleSections(prev => new Set([...prev, section.getAttribute('data-section')]));
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const executeCaptcha = () => {
    return new Promise((resolve) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute("6LcIY4EsAAAAADoIsHMQruBeXbRjcC2MdVvnJ0j7", { action: "contact" })
          .then((token) => {
            resolve(token);
          });
      });
    });
  };

  useEffect(() => {

    const termsSection = document.getElementById("terms");
    const privacySection = document.getElementById("privacy");

    if (!termsSection || !privacySection) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {
            setActiveDocument(entry.target.id);
          }

        });

      },
      {
        root: null,
        threshold: 0.15,
      }
    );

    observer.observe(termsSection);
    observer.observe(privacySection);

    return () => {
      observer.disconnect();
    };

  }, []);

  const [contactData, setContactData] = useState(true);
  
  const fetchContactData = async () => {
    
      try {
          const res = await fetch(
              `${process.env.REACT_APP_BASE_URL}api/contactdata/1`
          );
  
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  
          const json = await res.json();
          setContactData(json.data || []);
  
      } catch (err) {
          console.error("Fetch Running error:", err);
      }
    };
    
    useEffect(() => {
      fetchContactData();
    }, []); 

  return (
    <div className="min-h-screen bg-white text-white pt-20">
      {/* Header Section */}
      <section 
        data-section="header"
        className="relative
          h-[32vh] md:h-[40vh] lg:h-[45vh]
          overflow-hidden
          flex items-center justify-center
          bg-white"
      >
        <div className="absolute inset-0">
          <img
            src={`${process.env.REACT_APP_BASE_URL}uploads/images/${sectionHero.image}`}
            alt="background"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-slate-950/60"></div>

          {/* Gradient Glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-white"></div>

          {/* Optional Glow Accent */}
          <div className="absolute top-1/4 left-1/3 w-72 h-72 bg-blue-500/20 blur-[120px] rounded-full"></div>
          <div className="absolute bottom-1/4 right-1/3 w-72 h-72 bg-red-500/20 blur-[120px] rounded-full"></div>
        </div>        

        <div className={`
            relative z-10
            px-6
            text-center
            max-w-5xl
            mx-auto
            translate-y-[2vh]
            transition-all duration-1000
            ${
              visibleSections.has("header")
                ? "opacity-100"
                : "opacity-0 translate-y-[5vh]"
            }
          `}>
          <h1 className="
              font-bricolage
              font-bold
              tracking-tight
              leading-tight
              mb-4
              bg-gradient-to-r
              from-red-400 via-white to-blue-800
              bg-clip-text text-transparent
              text-[clamp(2rem,6vw,4.5rem)]">
            {sectionHero.title}
          </h1>
          <div className="w-16 md:w-24 h-1 rounded-full bg-gradient-to-r from-blue-500 to-red-500 mx-auto mb-5"></div>
          <p
            className="
            mx-auto
            max-w-xs sm:max-w-2xl
            text-white
            leading-relaxed
            text-[clamp(.95rem,1.8vw,1.15rem)]
            "
          >
            {sectionHero.description}
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section 
        data-section="contact"
        className="py-16 md:py-24 bg-gradient-to-b from-slate-100/95 to-slate-200/95"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 delay-200 ${
            visibleSections.has('contact')
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
            <div className="relative">

              {/* =====================================================
                  DESKTOP LAYOUT
              ====================================================== */}

              <div className="
                max-w-7xl
                mx-auto
                px-4
                sm:px-6
                lg:px-8
                md:grid
                md:grid-cols-[220px_minmax(0,1fr)]
                md:gap-8
              ">


                {/* =====================================================
                    SIDEBAR DESKTOP
                ====================================================== */}

                <aside className="hidden md:block">

                  <div className="sticky top-6">

                    <div className="
                      rounded-2xl
                      border border-black
                      p-3
                      bg-white text-slate-700
                    ">

                      <div className="
                        px-3
                        py-2
                        mb-2
                        text-[10px]
                        uppercase
                        tracking-widest
                      ">
                        Dokumen
                      </div>


                      <nav className="space-y-1">

                        {/* TERMS */}
                        <a
                          href="#terms"
                          className="
                            flex
                            items-center
                            gap-3
                            px-3
                            py-3
                            rounded-xl
                            text-sm
                            hover:text-blue-400
                            hover:bg-blue-400/[0.06]
                            transition
                          "
                        >

                          <div className="
                            w-8
                            h-8
                            rounded-lg
                            flex
                            items-center
                            bg-blue-400/10
                            justify-center
                          ">
                            📄
                          </div>

                          <div>
                            <div className="text-slate-700">
                              Syarat & Ketentuan
                            </div>

                            <div className="text-[10px] text-slate-600 mt-0.5">
                              Terms & Conditions
                            </div>
                          </div>

                        </a>


                        {/* PRIVACY */}
                        <a
                          href="#privacy"
                          className="
                            flex
                            items-center
                            gap-3
                            px-3
                            py-3
                            rounded-xl
                            text-sm
                            text-slate-400
                            hover:text-blue-400
                            hover:bg-blue-400/[0.06]
                            transition
                          "
                        >

                          <div className="
                            w-8
                            h-8
                            rounded-lg
                            bg-blue-400/10
                            flex
                            items-center
                            justify-center
                          ">
                            <FcPrivacy />
                          </div>

                          <div>
                            <div className="text-slate-700">
                              Privacy Policy
                            </div>

                            <div className="text-[10px] text-gray-600 mt-0.5">
                              Kebijakan Privasi
                            </div>
                          </div>

                        </a>

                      </nav>

                    </div>

                  </div>

                </aside>


                {/* =====================================================
                    MAIN CONTENT
                ====================================================== */}

                <main className="
                  min-w-0
                  pb-24
                  md:pb-0
                ">


                  {/* =====================================================
                      01 - SYARAT DAN KETENTUAN
                  ====================================================== */}

                  <section
                    id="terms"
                    className="scroll-mt-6"
                  >

                    <div className="space-y-6">


                      {/* =================================================
                          HEADER
                      ================================================= */}

                      <Section
                        id="terms-intro"
                        number="01"
                        title="Syarat dan Ketentuan Pengguna"
                        icon={
                          <span className="text-xl">
                            📄
                          </span>
                        }
                      >

                        <div className="space-y-5">

                          <p>
                            Selamat datang di <strong className='text-[#3E3274]'>www.kenziekomunika.com</strong>. Pengguna diwajibkan untuk membaca seluruh Syarat dan Ketentuan ini dengan saksama dan dapat menghubungi Customer Service Kenzie Komunika jika memiliki pertanyaan.
                          </p>


                          <p>
                            Syarat dan Ketentuan (Terms & Conditions) ini mengatur pemakaian, akses, dan jasa terhadap aplikasi, website, konten, serta produk yang ditawarkan oleh <strong className='text-[#3E3274]'>CV Kenzie Komunika</strong> selaku pemilik, pengembang, dan pengelola layanan Kenzie Komunika.
                          </p>


                          <p>
                            Dengan mendaftar, mengakses, dan/atau menggunakan layanan <strong className='text-[#3E3274]'>Kenzie Komunika</strong>, maka Pengguna dianggap telah membaca, mengerti, memahami, dan menyetujui seluruh isi dalam dokumen ini. Pengguna setuju dan mengikatkan diri secara sah menurut hukum untuk mematuhi Syarat dan Ketentuan ini, beserta Kebijakan Privasi (Privacy Policy) yang tertera pada halaman yang terpisah.
                          </p>


                          <p>
                           Bagi Pengguna yang tidak menyetujui Syarat dan Ketentuan ini, baik sebagian maupun seluruhnya, dipersilakan untuk berhenti menggunakan layanan dan menghapus aplikasi Kenzie Komunika dari perangkat Anda.
                          </p>

                        </div>

                      </Section>


                      {/* =================================================
                          I. DEFINISI
                      ================================================= */}

                      <Section
                        id="terms-definition"
                        number="02"
                        title="Definisi"
                      >

                        <div className="space-y-5">

                          <p>
                            Dalam Syarat dan Ketentuan ini, kecuali ditentukan lain oleh konteks kalimatnya, istilah-istilah di bawah ini memiliki arti dan batasan sebagai berikut:
                          </p>


                          <div className="space-y-3">

                            {[
                              [
                                "CV Kenzie Komunika ",
                                "CV Kenzie Komunika  adalah badan hukum sah yang memiliki, mengembangkan, dan mengelola operasional aplikasi Kenzie Komunika."
                              ],
                              [
                                "Aplikasi",
                                "Aplikasi adalah perangkat lunak berbasis digital bernama KENZIE KOMUNIKA (termasuk segala pembaruan dan modifikasinya) yang dikelola oleh Perusahaan sebagai sarana perdagangan produk digital."
                              ],
                              [
                                "Pengguna",
                                "Pengguna adalah pihak (perorangan atau badan usaha) yang telah mendaftar, memiliki akun terverifikasi pada Aplikasi, dan secara sah menggunakan Layanan."
                              ],
                              [
                                "Layanan",
                                "Layanan adalah fasilitas sistem elektronik berupa penyediaan akses pembelian dan/atau pembayaran Produk Digital yang disediakan oleh Perusahaan melalui Aplikasi."
                              ],
                              [
                                "Produk Digital",
                                "Produk Digital adalah barang tidak berwujud atau jasa berbasis digital yang diperdagangkan di dalam Aplikasi, termasuk namun tidak terbatas pada pulsa prabayar, paket data internet, token listrik (PLN), dan pembayaran tagihan rutin (PPOB)."
                              ],
                              [
                                "Stok",
                                "Stok adalah representasi nilai kuantitas Produk Digital yang telah dibeli oleh Pengguna di muka (prabayar) untuk mempermudah proses Transaksi di dalam Aplikasi. Stok secara tegas dinyatakan bukan merupakan uang elektronik (e-money), simpanan, tabungan, maupun deposito perbankan, serta tidak dijamin oleh Lembaga Penjamin Simpanan (LPS)."
                              ],
                              [
                                "Transaksi",
                                "Transaksi adalah proses eksekusi pembelian, pengisian, atau pembayaran Produk Digital ke nomor tujuan yang diinput oleh Pengguna, menggunakan pengurangan Stok yang tersedia di dalam akun Pengguna."
                              ],
                              [
                                "Penyedia Layanan Pihak Ketiga",
                                "Penyedia Layanan Pihak Ketiga adalah entitas eksternal seperti operator telekomunikasi (provider seluler), PLN, penyedia biller, atau agregator lain yang memproduksi dan menerbitkan Produk Digital yang diperdagangkan di dalam Aplikasi."
                              ],
                              [
                                "Tiket",
                                "Tiket adalah instruksi pemesanan atau permintaan penambahan Stok yang diinisiasi oleh Pengguna di dalam sistem Aplikasi sebelum melakukan transfer pembayaran. Tiket memiliki batas waktu kedaluwarsa dan menjadi rujukan sistem untuk memproses penambahan Stok."
                              ],
                              [
                                "Kode Unik",
                                "Kode Unik adalah nominal angka khusus (biasanya berada pada 2-3 digit terakhir nominal transfer) yang dihasilkan secara otomatis oleh sistem saat Pengguna membuat Tiket. Kode Unik berfungsi sebagai identifikasi mutasi bank agar pembayaran Pengguna dapat diverifikasi secara sistem dan Stok bertambah secara otomatis ke akun yang tepat."
                              ]
                            ].map(([title, description], index) => (

                              <div
                                key={title}
                                className="
                                  rounded-xl
                                  border
                                  border-white/10
                                  bg-white/[0.02]
                                  p-4
                                "
                              >

                                <div className="
                                  flex
                                  gap-3
                                  items-start
                                ">

                                  <div className="
                                    flex-shrink-0
                                    w-7
                                    h-7
                                    rounded-lg
                                    bg-blue-400/10
                                    flex
                                    items-center
                                    justify-center
                                    text-xs
                                    text-blue-400
                                  ">
                                    {index + 1}
                                  </div>

                                  <div>

                                    <div className="
                                      text-white
                                      font-semibold
                                    ">
                                      {title}
                                    </div>

                                    <p className="
                                      mt-1
                                      text-sm
                                      text-gray-500
                                      leading-6
                                    ">
                                      {description}
                                    </p>

                                  </div>

                                </div>

                              </div>

                            ))}

                          </div>

                        </div>

                      </Section>


                      {/* =================================================
                          II. AKUN DAN REGISTRASI
                      ================================================= */}

                      <Section
                        id="terms-account"
                        number="03"
                        title="Ketentuan Akun dan Registrasi"
                      >

                        <div className="space-y-6">

                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              1. Persyaratan Pengguna
                            </h3>

                            <p>
                              Untuk dapat menggunakan layanan Aplikasi Kenzie Komunika, Pengguna wajib melakukan pendaftaran akun dengan memberikan data yang valid, akurat, dan dapat dipertanggungjawabkan (seperti nomor telepon seluler yang aktif). Pengguna menyatakan bahwa dirinya adalah individu yang cakap menurut hukum untuk mengikatkan diri dalam sebuah perjanjian yang sah.
                            </p>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              2. Keamanan Kredensial Akun
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Pengguna bertanggung jawab penuh secara pribadi
                                untuk menjaga kerahasiaan seluruh informasi keamanan
                                akun, termasuk namun tidak terbatas pada Kata Sandi
                                (<em>Password</em>), PIN Transaksi, dan Kode OTP
                                (<em>One Time Password</em>).
                              </li>

                              <li>
                                Perusahaan dan seluruh staf representatif CV Kenzie Komunika {" "}
                                <strong className="text-[#3E3274]">
                                  tidak akan pernah meminta
                                </strong>{" "}
                                Kata Sandi, PIN, atau Kode OTP Pengguna dengan alasan
                                apa pun.
                              </li>

                              <li>
                                Segala kerugian atau hilangnya Stok yang timbul
                                akibat kelalaian Pengguna dalam menjaga kerahasiaan
                                akun (termasuk namun tidak terbatas pada terkena
                                jebakan penipuan/<em>phishing</em>, meminjamkan akun
                                kepada pihak lain, atau memberikan OTP kepada
                                siapapun) sepenuhnya menjadi{" "}
                                <strong className="text-[#3E3274]">
                                  tanggung jawab mutlak Pengguna
                                </strong>
                                , dan Perusahaan dibebaskan dari segala tuntutan
                                ganti rugi.
                              </li>

                            </ul>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              3. Validitas Aktivitas Akun
                            </h3>

                            <p>
                              Setiap Transaksi dan aktivitas yang dilakukan melalui
                              akun Pengguna akan dianggap sah sebagai instruksi
                              langsung dari Pengguna yang bersangkutan. Perusahaan
                              memiliki hak penuh untuk memproses instruksi tersebut
                              tanpa wajib melakukan verifikasi tambahan di luar
                              sistem yang telah berjalan di dalam Aplikasi.
                            </p>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              4. Penangguhan dan Pemblokiran Akun
                            </h3>

                            <p className="mb-3">
                              Perusahaan memiliki hak mutlak untuk melakukan
                              tindakan pembatasan layanan, penangguhan (suspend),
                              hingga pemblokiran akun secara sepihak tanpa
                              pemberitahuan sebelumnya apabila:
                            </p>

                            <ul className="space-y-3">

                              <li>
                                Ditemukan adanya pelanggaran terhadap salah satu,
                                sebagian, atau seluruh isi Syarat dan Ketentuan ini.
                              </li>

                              <li>
                                Terdapat indikasi aktivitas mencurigakan, kecurangan
                                (fraud), manipulasi sistem, atau penggunaan bug pada
                                Aplikasi.
                              </li>

                              <li>
                                Terdeteksi adanya pola transaksi, aliran dana, atau
                                aktivitas akun yang mengindikasikan keterkaitan
                                dengan praktik Judi Online (Judol), Tindak Pidana
                                Pencucian Uang (TPPU), atau kejahatan siber lainnya.
                              </li>

                              <li>
                                Terdapat permintaan resmi dari otoritas hukum atau
                                instansi negara yang berwenang.
                              </li>

                            </ul>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              5. Pelepasan Tanggung Jawab Terhadap Penyalahgunaan
                              Ilegal
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Aplikasi KENZIE KOMUNIKA disediakan semata-mata sebagai
                                platform untuk memfasilitasi transaksi jual-beli
                                Produk Digital. CV Kenzie Komunika  beserta
                                seluruh jajaran direksi, manajemen, dan karyawannya
                                dibebaskan dari segala bentuk tuntutan hukum, ganti
                                rugi, pidana, maupun perdata atas segala aktivitas
                                ilegal yang dilakukan oleh Pengguna.
                              </li>

                              <li>
                                Segala bentuk kesalahan, kelalaian, kesengajaan,
                                maupun penyalahgunaan Aplikasi oleh Pengguna untuk
                                tujuan yang melanggar hukum, termasuk namun tidak
                                terbatas pada memfasilitasi deposit/transaksi Judi
                                Online, pencucian uang, pendanaan terorisme, atau
                                penipuan, adalah murni tanggung jawab pribadi
                                Pengguna sepenuhnya.
                              </li>

                              <li>
                                Perusahaan tidak memiliki kewajiban untuk memantau
                                niat, tujuan akhir, atau afiliasi dari setiap
                                Transaksi yang dilakukan Pengguna di luar ekosistem
                                sistem Aplikasi Kenzie Komunika.
                              </li>

                            </ul>

                          </div>

                        </div>

                      </Section>


                      {/* =================================================
                          III. STOK DAN TRANSAKSI
                      ================================================= */}

                      <Section
                        id="terms-transaction"
                        number="04"
                        title="Ketentuan Penambahan Stok dan Transaksi"
                      >

                        <div className="space-y-6">

                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              1. Prosedur Penambahan Stok
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Pengguna diwajibkan untuk membuat permintaan{" "}
                                <strong className="text-[#3E3274]">
                                  Tiket
                                </strong>{" "}
                                melalui Aplikasi sebelum melakukan transfer dana
                                untuk penambahan Stok.
                              </li>

                              <li>
                                Pengguna wajib mentransfer dana{" "}
                                <strong className="text-[#3E3274]">
                                  sama persis
                                </strong>{" "}
                                dengan nominal tagihan yang tertera pada Tiket,
                                yang telah menyertakan Kode Unik pada beberapa digit
                                terakhir.
                              </li>

                              <li>
                                Transfer yang dilakukan sesuai dengan instruksi
                                Tiket dan Kode Unik akan diproses dan ditambahkan
                                ke dalam Stok Pengguna secara otomatis oleh sistem.
                              </li>

                            </ul>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              2. Kesalahan Transfer Penambahan Stok
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Apabila Pengguna mentransfer dana dengan nominal
                                yang berbeda dari Tiket (misalnya membulatkan
                                nominal, melupakan Kode Unik, atau salah ketik),
                                sistem tidak dapat memproses penambahan Stok secara
                                otomatis.
                              </li>

                              <li>
                                Atas kesalahan tersebut, Pengguna wajib melakukan
                                konfirmasi manual kepada Layanan Pelanggan
                                (<em>Customer Service</em>) resmi Perusahaan dengan
                                melampirkan bukti transfer asli.
                              </li>

                              <li>
                                Perusahaan tidak bertanggung jawab atas hilangnya
                                dana akibat kelalaian Pengguna dalam mentransfer ke
                                nomor rekening yang salah, rekening yang tidak
                                terdaftar atas nama Perusahaan, atau menjadi korban
                                penipuan pihak ketiga yang mengatasnamakan Radar
                                Pulsa.
                              </li>

                            </ul>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              3. Kebijakan Input Data dan Sifat Final Transaksi
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Pengguna bertanggung jawab penuh secara mutlak atas
                                kebenaran data nomor tujuan (nomor HP, ID Pelanggan
                                PLN, nomor meter, dsb) sebelum mengeksekusi
                                Transaksi.
                              </li>

                              <li>
                                Setiap Transaksi yang telah diproses oleh sistem dan
                                berstatus{" "}
                                <strong className="text-[#3E3274]">
                                  "Sukses"
                                </strong>
                                ,{" "}
                                <strong className="text-[#3E3274]">
                                  bersifat final dan tidak dapat dibatalkan, diubah,
                                  maupun ditarik kembali
                                </strong>.
                              </li>

                              <li>
                                Apabila terjadi kesalahan <em>input</em> nomor tujuan
                                oleh Pengguna yang mengakibatkan Produk Digital
                                terkirim ke pihak yang salah, maka hal tersebut
                                sepenuhnya menjadi kelalaian dan kerugian Pengguna.
                                Perusahaan tidak dapat memberikan ganti rugi atau
                                pengembalian Stok.
                              </li>

                            </ul>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              4. Ketergantungan Sistem Pihak Ketiga dan Penanganan
                              Gangguan
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Pengguna menyadari dan memahami bahwa pemrosesan
                                Produk Digital bergantung penuh pada ketersediaan
                                dan kestabilan sistem dari Penyedia Layanan Pihak
                                Ketiga (Operator Seluler, PLN, dan Agregator
                                terkait).
                              </li>

                              <li>
                                Jika Transaksi tertunda (<em>pending</em>) akibat
                                perawatan jaringan (<em>maintenance</em>) atau
                                gangguan dari Penyedia Layanan Pihak Ketiga,
                                Pengguna wajib menunggu hasil pengecekan sistem
                                sesuai dengan batas waktu investigasi yang berlaku
                                dari penyedia layanan tersebut.
                              </li>

                              <li>
                                Apabila Transaksi pada akhirnya dinyatakan{" "}
                                <strong className="text-[#3E3274]">
                                  Gagal
                                </strong>{" "}
                                oleh Penyedia Layanan Pihak Ketiga, maka Stok yang
                                sebelumnya terpotong akan dikembalikan
                                (<em>refund</em>) secara penuh ke akun Pengguna.
                              </li>

                            </ul>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              5. Sifat Stok dan Larangan Tarik Tunai (No Cash-Out)
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Sejalan dengan Definisi Stok pada bagian awal, Stok
                                yang terdapat di dalam akun Pengguna secara eksklusif
                                hanya dapat digunakan untuk melakukan Transaksi
                                Produk Digital di dalam Aplikasi.
                              </li>

                              <li>
                                Sisa Stok{" "}
                                <strong className="text-[#3E3274]">
                                  tidak dapat ditarik tunai, dicairkan kembali ke
                                  rekening bank (cash-out), atau dikembalikan dalam
                                  bentuk uang tunai
                                </strong>{" "}
                                dengan alasan apa pun, kecuali dalam konteks
                                pengembalian internal ke akun Aplikasi akibat
                                Transaksi yang berstatus Gagal.
                              </li>

                            </ul>

                          </div>

                        </div>

                      </Section>


                      {/* =================================================
                          IV. HARGA
                      ================================================= */}

                      <Section
                        id="terms-price"
                        number="05"
                        title="Ketentuan Harga dan Perubahan Layanan"
                      >

                        <div className="space-y-6">

                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              1. Kebijakan Harga
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Harga Produk Digital yang tertera di dalam Aplikasi
                                KENZIE KOMUNIKA bersifat fluktuatif dan dapat berubah
                                sewaktu-waktu tanpa pemberitahuan sebelumnya.
                                Perubahan harga ini menyesuaikan dengan kebijakan
                                penetapan harga dari Penyedia Layanan Pihak Ketiga
                                (Operator dan <em>Biller</em>).
                              </li>

                              <li>
                                Pengguna menyetujui bahwa harga final yang mengikat
                                adalah harga yang tertera pada layar konfirmasi
                                sesaat sebelum Pengguna menekan tombol eksekusi
                                Transaksi atau memasukkan PIN.
                              </li>

                            </ul>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              2. Ketersediaan Produk dan Fitur
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Perusahaan berhak penuh untuk menambah, memodifikasi,
                                menangguhkan, atau menghapus ketersediaan suatu
                                Produk Digital maupun fitur layanan di dalam Aplikasi
                                sewaktu-waktu.
                              </li>

                              <li>
                                Perusahaan tidak dapat dituntut ganti rugi atas
                                hilangnya potensi keuntungan Pengguna akibat
                                ditutupnya suatu produk, gangguan <em>server</em>
                                pusat, atau perubahan sistem layanan.
                              </li>

                            </ul>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              3. Pembaruan Aplikasi
                            </h3>

                            <p>
                              Demi menjaga keamanan dan kenyamanan bertransaksi,
                              Perusahaan akan melakukan pembaruan (<em>update</em>)
                              Aplikasi secara berkala. Pengguna disarankan untuk
                              selalu menggunakan versi Aplikasi terbaru. Perusahaan
                              tidak bertanggung jawab atas kendala Transaksi yang
                              disebabkan oleh penggunaan versi Aplikasi yang sudah
                              usang (<em>outdated</em>).
                            </p>

                          </div>

                        </div>

                      </Section>


                      {/* =================================================
                          V. HUKUM
                      ================================================= */}

                      <Section
                        id="terms-law"
                        number="06"
                        title="Hukum yang Mengatur dan Penyelesaian Sengketa"
                      >

                        <div className="space-y-6">

                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              1. Hukum yang Berlaku
                            </h3>

                            <p>
                              Syarat dan Ketentuan ini, beserta segala pelaksanaannya,
                              tunduk dan ditafsirkan berdasarkan yurisdiksi hukum
                              negara Republik Indonesia.
                            </p>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              2. Penyelesaian Sengketa
                            </h3>

                            <ul className="space-y-3">

                              <li>
                                Apabila di kemudian hari timbul perselisihan atau
                                sengketa akibat pelaksanaan Syarat dan Ketentuan ini,
                                Perusahaan dan Pengguna sepakat untuk menyelesaikannya
                                secara musyawarah untuk mufakat melalui jalur Layanan
                                Pelanggan (<em>Customer Service</em>) resmi Perusahaan.
                              </li>

                              <li>
                                Apabila musyawarah tidak mencapai titik temu dalam
                                kurun waktu 30 (tiga puluh) hari kalender, maka kedua
                                belah pihak sepakat untuk menyerahkan penyelesaian
                                sengketa tersebut secara eksklusif kepada yurisdiksi
                                Pengadilan Negeri Bandung.
                              </li>

                            </ul>

                          </div>

                        </div>

                      </Section>


                      {/* =================================================
                          VI. PENUTUP
                      ================================================= */}

                      <Section
                        id="terms-closing"
                        number="07"
                        title="Penutup"
                      >

                        <div className="space-y-6">

                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              1. Perubahan Syarat dan Ketentuan
                            </h3>

                            <p>
                              Perusahaan berhak untuk menambah, mengubah, atau
                              memperbarui isi Syarat dan Ketentuan ini sewaktu-waktu.
                              Segala bentuk perubahan akan diinformasikan kepada
                              Pengguna melalui notifikasi di dalam Aplikasi atau
                              saluran komunikasi resmi lainnya. Penggunaan layanan
                              secara berkelanjutan setelah perubahan tersebut berlaku
                              dianggap sebagai bentuk persetujuan mutlak Pengguna
                              terhadap Syarat dan Ketentuan yang baru.
                            </p>

                          </div>


                          <div>

                            <h3 className="text-[#3E3274] font-semibold mb-3">
                              2. Kontak Resmi
                            </h3>

                            <p>
                              Segala bentuk keluhan, pertanyaan, maupun kendala
                              terkait Aplikasi KENZIE KOMUNIKA hanya dilayani melalui
                              saluran resmi (<em>Customer Service</em>) yang tertera
                              di dalam Aplikasi. Perusahaan tidak melayani klaim atau
                              komplain di luar saluran komunikasi resmi.
                            </p>

                          </div>

                        </div>

                      </Section>


                      {/* =================================================
                          END OF TERMS
                      ================================================= */}

                      <div className="
                        my-12
                        border-t
                        border-white/10
                      " />

                    </div>

                  </section>


                  {/* =====================================================
                      02 - PRIVACY POLICY
                      
                      
                  ====================================================== */}

                  <div className="space-y-6">
                    {/* =====================================================
                        01 - INDONESIAN
                    ====================================================== */}
                    <Section
                      id="privacy"
                      number="01"
                      title="Privasi Anda Penting Bagi Kami"
                      icon={
                        <FcPrivacy/>
                      }
                    >

                      <div className="space-y-5">

                        <p>
                          KENZIE KOMUNIKA dan Exlusoft ("kami" atau "kita")
                          berdedikasi untuk melindungi hak-hak privasi
                          pengguna aplikasi kami ("pengguna", "user" atau
                          "Anda").
                        </p>

                        <p>
                          Kebijakan Privasi ini ("Kebijakan") menjelaskan
                          cara kami mengumpulkan, menyimpan, menggunakan,
                          dan mengelola informasi, termasuk informasi pribadi,
                          yang Anda berikan atau kami kumpulkan sehubungan
                          dengan aplikasi Android dan webreport ("Layanan")
                          kami.
                        </p>

                        <p>
                          Kebijakan ini hanya terbatas untuk informasi yang
                          dikumpulkan atau diterima oleh KENZIE KOMUNIKA melalui
                          penggunaan Layanan kami. Kami tidak bertanggung
                          jawab atas tindakan orang lain atau pihak ketiga,
                          isi atau materi pada website pihak ketiga yang
                          berafiliasi dengan Layanan kami, produk atau jasa
                          yang mereka tawarkan, maupun penggunaan informasi
                          yang Anda berikan kepada mereka.
                        </p>


                        {/* Official App */}
                        <div className="rounded-xl border border-blue-400/10 bg-blue-400/[0.04] p-4">

                          <div className="text-xs uppercase tracking-wider text-blue-400 mb-2">
                            Aplikasi Resmi
                          </div>

                          <p>
                            Aplikasi Android resmi kami hanya yang bernama{" "}
                            <strong className="text-[#3E3274]">
                              KENZIE KOMUNIKA
                            </strong>.
                          </p>

                        </div>


                        <p>
                          Dengan menggunakan Layanan kami, Anda dianggap
                          telah menyetujui kebijakan ini dan mengizinkan kami
                          melakukan pengolahan data, termasuk informasi pribadi
                          Anda, dengan cara yang dijelaskan dalam Kebijakan ini.
                        </p>

                        <p>
                          Apabila Anda tidak menyetujui ketentuan atau Kebijakan
                          kami, maka silakan untuk tidak menggunakan Layanan kami.
                        </p>

                        <p>
                          Jika Anda memiliki pertanyaan atau kekhawatiran
                          tentang kebijakan privasi kami, Anda dapat langsung
                          menghubungi kami melalui SMS, instant messaging
                          (chat), atau melalui aplikasi Android.
                        </p>

                        <p>
                          Informasi Pribadi yang kami kumpulkan digunakan
                          untuk menyediakan dan meningkatkan Layanan, serta
                          dalam rangka keamanan dan pencegahan penipuan.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        02 - ENGLISH
                    ====================================================== */}
                    <Section
                      id="english"
                      number="02"
                      title="Your Privacy Is Important To Us"
                      icon={
                        <FcPrivacy/>
                      }
                    >

                      <div className="space-y-5">

                        <p>
                          KENZIE KOMUNIKA and Exlusoft ("we" or "us") are dedicated
                          to protecting the privacy rights of our application
                          users ("user", "user" or "you").
                        </p>

                        <p>
                          This Privacy Policy ("Policy") describes how we
                          collect, store, use and manage information, including
                          personal information, that you provide or we collect
                          in connection with our Android application and
                          webreport ("Service").
                        </p>

                        <p>
                          This policy is limited to information collected or
                          received by KENZIE KOMUNIKA through the use of our Services.
                          We are not responsible for the actions of other people
                          or third parties, the content or material on third-party
                          websites affiliated with our Services, the products or
                          services they offer, or the use of information you
                          provide to them.
                        </p>


                        {/* Official Android */}
                        <div className="rounded-xl border border-blue-400/10 bg-blue-400/[0.04] p-4">

                          <div className="text-xs uppercase tracking-wider text-blue-400 mb-2">
                            Official Android Application
                          </div>

                          <p>
                            Our only official Android application is named{" "}
                            <strong className="text-[#3E3274]">
                              KENZIE KOMUNIKA
                            </strong>.
                          </p>

                        </div>


                        <p>
                          By using our Services, you are deemed to have agreed
                          to this policy and allow us to process data, including
                          your personal information, in the manner described
                          in this Policy.
                        </p>

                        <p>
                          If you do not agree to our terms or policies, please
                          do not use our services.
                        </p>

                        <p>
                          The Personal Information we collect is used to provide
                          and improve the Service, as well as for security and
                          fraud prevention purposes.
                        </p>

                        <p>
                          We will not use or share your information with any
                          third party except as described in this Privacy Policy.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        03 - DATA COLLECTION
                    ====================================================== */}
                    <Section
                      id="data"
                      number="03"
                      title="Pengumpulan Data dan Tujuan Penggunaan Data"
                    >

                      <div className="space-y-5">

                        <p>
                          KENZIE KOMUNIKA mengumpulkan informasi sebagaimana
                          dijelaskan di bawah ini. Tujuan utama kami dalam
                          mengumpulkan dan menggunakan informasi Anda adalah
                          untuk membuat akun sebagai agen kami, memfasilitasi
                          transaksi pembelian maupun pembayaran secara
                          elektronik, memberikan pelayanan, meningkatkan
                          kualitas layanan, menghubungi Anda, melakukan
                          pengamatan dan pengawasan, serta membuat laporan
                          untuk keperluan manajemen internal.
                        </p>

                        <p>
                          Kami menyimpan informasi pada server kami yang
                          dibackup dengan server yang berlokasi di data center
                          Singapura. Kami dapat menambah atau memindahkan
                          server sesuai dengan kondisi dan kebutuhan kami.
                        </p>


                        <div>

                          <h3 className="text-[#3E3274] font-semibold mb-4">
                            Data yang Dikumpulkan
                          </h3>


                          <div className="grid sm:grid-cols-2 gap-3">

                            {[
                              [
                                "Nama",
                                "Fungsional aplikasi, manajemen akun dan pencegahan penipuan."
                              ],
                              [
                                "Nomor Telepon",
                                "Fungsional aplikasi, manajemen akun dan pencegahan penipuan."
                              ],
                              [
                                "Alamat",
                                "Fungsional aplikasi, manajemen akun dan pencegahan penipuan."
                              ],
                              [
                                "ID Pengguna",
                                "Verifikasi akun dan pencegahan penipuan."
                              ],
                              [
                                "Daftar Kontak",
                                "Mempermudah proses transaksi."
                              ]
                            ].map(([title, description]) => (

                              <div
                                key={title}
                                className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                              >

                                <div className="text-[#3E3274] font-medium">
                                  {title}
                                </div>

                                <div className="text-xs text-gray-500 mt-1 leading-5">
                                  {description}
                                </div>

                              </div>

                            ))}

                          </div>

                        </div>


                        <p>
                          Ketika melakukan pendaftaran atau login, informasi
                          yang dikumpulkan meliputi Data Pribadi dan informasi
                          lainnya yang terkumpul secara otomatis karena Anda
                          mengakses dan/atau menggunakan Aplikasi.
                        </p>

                        <p>
                          Informasi yang diperoleh secara otomatis dapat meliputi
                          alamat Internet Protocol (IP), informasi login,
                          lokasi geografis, sistem operasi, versi browser,
                          lalu lintas data, rekaman situs dan data komunikasi
                          lainnya.
                        </p>

                        <p>
                          Untuk dapat menggunakan Aplikasi, kami dapat meminta
                          Pengguna untuk mengaktifkan cookies. Apabila Pengguna
                          menolak cookies, akses terhadap fungsi tertentu pada
                          Aplikasi dapat terpengaruh atau terhenti.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        04 - IMAGE
                    ====================================================== */}
                    <Section
                      id="image"
                      number="04"
                      title="Informasi Gambar"
                    >

                      <div className="space-y-5">

                        <p>
                          Ketika dibutuhkan dan dengan adanya persetujuan
                          penggunaan kamera dari pengguna, aplikasi dapat
                          mengakses kamera untuk mengambil foto ID Pengguna
                          seperti KTP atau SIM untuk keperluan verifikasi akun.
                        </p>

                        <p>
                          Gambar atau foto yang telah Anda ambil akan diupload
                          ke server kami agar kami dapat mengecek dan
                          memverifikasi akun.
                        </p>

                        <p>
                          Kami tidak menggunakan izin atau hak akses kamera
                          selain untuk melakukan pengambilan gambar untuk
                          tujuan verifikasi akun. Izin kamera juga diperlukan
                          ketika aplikasi membutuhkan fungsi kamera untuk
                          melakukan scan barcode atau QR Code.
                        </p>


                        <div className="rounded-xl border border-blue-400/10 bg-blue-400/[0.04] p-4">

                          <div className="text-xs uppercase tracking-wider text-blue-400 mb-1">
                            User Control
                          </div>

                          <p className="text-sm">
                            Pengguna dapat menonaktifkan izin kamera kapanpun
                            pengguna menginginkannya melalui pengaturan perangkat.
                          </p>

                        </div>

                      </div>

                    </Section>



                    {/* =====================================================
                        05 - CONTACTS
                    ====================================================== */}
                    <Section
                      id="contacts"
                      number="05"
                      title="Daftar Kontak"
                    >

                      <div className="space-y-5">

                        <p>
                          Ketika dibutuhkan oleh pengguna, aplikasi kami dapat
                          mengakses daftar kontak dan mengupload daftar kontak
                          terpilih dengan adanya persetujuan dari pengguna.
                        </p>

                        <p>
                          Akses ini digunakan untuk mempermudah proses transaksi
                          agar pengisian data transaksi dapat dilakukan dengan
                          lebih cepat dan praktis.
                        </p>

                        <p>
                          Kami tidak akan menggunakan, membagikan, atau
                          menyebarkan data kontak kepada pihak yang tidak
                          terkait dengan layanan kami.
                        </p>

                        <p>
                          Pengguna berhak untuk menonaktifkan hak akses ini
                          ketika tidak menggunakan aplikasi kami.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        06 - DEVICE
                    ====================================================== */}
                    <Section
                      id="device"
                      number="06"
                      title="ID Perangkat atau Informasi Lainnya"
                    >

                      <div className="space-y-5">

                        <p>
                          Jika Anda menggunakan aplikasi Android kami, kami
                          dapat mengumpulkan dan mencatat informasi tertentu
                          seperti ID perangkat yang unik, jenis hardware,
                          tipe atau seri HP, MAC address, IMEI, versi sistem
                          operasi, nama perangkat, alamat email, serta lokasi
                          berdasarkan alamat IP maupun informasi jaringan.
                        </p>

                        <p>
                          Informasi ini membantu kami mengatasi masalah teknis,
                          memahami masalah kompatibilitas aplikasi pada perangkat
                          Anda, serta membantu mencegah penipuan.
                        </p>

                        <p>
                          Kami tidak akan membagikan informasi ini kepada pihak
                          ketiga tanpa izin Anda kecuali sebagaimana dijelaskan
                          dalam Kebijakan Privasi ini atau diwajibkan oleh hukum.
                        </p>

                        <p>
                          Jika Anda tidak ingin lagi kami menggunakan informasi
                          tersebut, Anda dapat membatasi izin melalui pengaturan
                          perangkat atau menghapus Aplikasi Android KENZIE KOMUNIKA.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        07 - PERMISSIONS
                    ====================================================== */}
                    <Section
                      id="permissions"
                      number="07"
                      title="Perizinan Aplikasi"
                    >

                      <div className="space-y-3">

                        {[
                          [
                            "Bluetooth",
                            "android.permission.BLUETOOTH",
                            "Digunakan untuk proses cetak struk."
                          ],
                          [
                            "Contact",
                            "android.permission.READ_CONTACTS",
                            "Digunakan untuk mempermudah pengisian nomor tujuan saat pembelian."
                          ],
                          [
                            "Lokasi",
                            "android.permission.ACCESS_FINE_LOCATION",
                            "Digunakan untuk membantu akurasi pengiriman produk fisik."
                          ],
                          [
                            "Penyimpanan",
                            "READ_EXTERNAL_STORAGE / WRITE_EXTERNAL_STORAGE",
                            "Digunakan untuk menyimpan hasil share struk."
                          ],
                          [
                            "Kamera",
                            "android.permission.CAMERA",
                            "Digunakan untuk mengambil gambar KTP/ID Pengguna dan melakukan scan voucher."
                          ]
                        ].map(([name, permission, description]) => (

                          <div
                            key={name}
                            className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                          >

                            <div className="flex flex-col sm:flex-row sm:items-start gap-3">

                              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-400/10 flex items-center justify-center">

                                <span className="text-blue-400 text-xs">
                                  ✓
                                </span>

                              </div>


                              <div>

                                <div className="text-[#3E3274] font-semibold">
                                  {name}
                                </div>

                                <code className="block mt-1 text-[11px] text-blue-400/70">
                                  {permission}
                                </code>

                                <p className="mt-2 text-sm text-gray-500">
                                  {description}
                                </p>

                              </div>

                            </div>

                          </div>

                        ))}

                      </div>


                      <div className="mt-5 space-y-5">

                        <p>
                          Aplikasi Android akan meminta izin yang diperlukan
                          untuk menghubungkan perangkat ke jaringan internet,
                          menyimpan data pada perangkat, mengirim informasi
                          pribadi, mengakses daftar kontak, meminta informasi
                          lokasi, serta mengirim notifikasi.
                        </p>

                        <p>
                          Saat browsing atau login ke webreport kami, sistem
                          operasi komputer, alamat IP, waktu akses, jenis browser
                          dan alamat rujukan situs dapat dicatat secara otomatis
                          oleh sistem.
                        </p>

                        <p>
                          Kami juga dapat meminta data pribadi seperti nama,
                          foto profil, jenis kelamin, usia atau tanggal lahir,
                          link profil pada situs jaringan sosial, username dan
                          email untuk keperluan pembuatan profil pengguna dan
                          pemberian layanan.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        08 - NOTIFICATION
                    ====================================================== */}
                    <Section
                      id="notification"
                      number="08"
                      title="Push Notification"
                    >

                      <div className="space-y-5">

                        <p>
                          Kami dapat mengirimkan pemberitahuan melalui aplikasi
                          Android untuk mengirim berbagai jenis informasi atau
                          pesan.
                        </p>

                        <p>
                          Anda dapat berhenti menerima notifikasi dari aplikasi
                          kami melalui pengaturan notifikasi pada perangkat Anda.
                        </p>

                        <p>
                          Aplikasi dapat menggunakan layanan pihak ketiga untuk
                          pengiriman notifikasi yang dapat mengumpulkan informasi
                          yang digunakan untuk mengidentifikasi Anda.
                        </p>

                        <a
                          href="https://policies.google.com/privacy"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition"
                        >
                          Google Play Services Privacy Policy
                          <span>↗</span>
                        </a>

                      </div>

                    </Section>



                    {/* =====================================================
                        09 - DISCLOSURE
                    ====================================================== */}
                    <Section
                      id="disclosure"
                      number="09"
                      title="Pengungkapan Informasi Anda"
                    >

                      <div className="space-y-5">

                        <p>
                          KENZIE KOMUNIKA tidak berbagi informasi pribadi Anda
                          kecuali disetujui oleh Anda atau sebagaimana dijelaskan
                          dalam Kebijakan Privasi ini.
                        </p>


                        <ul className="space-y-3">

                          {[
                            "KENZIE KOMUNIKA dapat bekerja sama dengan perusahaan atau individu untuk peningkatan kualitas Layanan kami yang mungkin membutuhkan akses terhadap informasi pribadi Anda.",

                            "Kami dapat memberikan informasi sebagaimana diizinkan atau diwajibkan oleh hukum, termasuk untuk penegakan hukum, penyelidikan penipuan, permintaan pemerintah, serta perlindungan hak dan keselamatan kami maupun pengguna.",

                            "KENZIE KOMUNIKA dapat berbagi informasi sehubungan dengan merger, penjualan aset, pembiayaan atau akuisisi seluruh atau sebagian bisnis kami.",

                            "Kami dapat berbagi informasi agregat anonim dengan pengiklan, penerbit, mitra bisnis, sponsor dan pihak ketiga lainnya."
                          ].map((item, index) => (

                            <li
                              key={index}
                              className="flex gap-3"
                            >

                              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />

                              <span>
                                {item}
                              </span>

                            </li>

                          ))}

                        </ul>

                      </div>

                    </Section>



                    {/* =====================================================
                        10 - SECURITY
                    ====================================================== */}
                    <Section
                      id="security"
                      number="10"
                      title="Keamanan"
                    >

                      <div className="space-y-5">

                        <p>
                          KENZIE KOMUNIKA mengambil langkah-langkah yang wajar
                          untuk melindungi informasi Anda dari akses yang tidak
                          sah, kehilangan, penyalahgunaan atau perubahan oleh
                          pihak ketiga.
                        </p>

                        <p>
                          Meskipun kami melakukan upaya dengan itikad baik
                          untuk menyimpan informasi yang dikumpulkan pada
                          lingkungan yang aman dan tidak tersedia untuk umum,
                          kami tidak dapat menjamin keamanan mutlak informasi
                          dalam proses transmisi atau penyimpanan.
                        </p>

                        <p>
                          Tidak ada metode transmisi melalui internet atau
                          metode penyimpanan elektronik yang 100% aman.
                          Oleh karena itu, kami tidak dapat menjamin keamanan
                          mutlak.
                        </p>


                        <div className="rounded-xl border border-blue-400/10 bg-blue-400/[0.04] p-5">

                          <div className="flex items-center gap-3 mb-3">

                            <div className="w-9 h-9 rounded-lg bg-blue-400/10 flex items-center justify-center">

                              <FcPrivacy/>

                            </div>

                            <h3 className="text-[#3E3274] font-semibold">
                              Secure Communication
                            </h3>

                          </div>


                          <p>
                            Kami berupaya melindungi komunikasi menggunakan
                            teknologi keamanan seperti Secure Socket Layer
                            (SSL) untuk membantu mengacak jalur komunikasi
                            sehingga tidak dapat dibaca oleh pihak lain.
                          </p>

                        </div>


                        <p>
                          Anda dilarang memberikan atau mengungkapkan identitas,
                          ID, password dan PIN kepada pihak lain dan harus selalu
                          menjaga keamanan perangkat elektronik yang digunakan.
                        </p>

                        <p>
                          Kami tidak akan pernah meminta Anda untuk menyerahkan
                          atau menyebarkan informasi personal atau keamanan Anda,
                          termasuk akun, ID, password dan PIN.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        11 - THIRD PARTY
                    ====================================================== */}
                    <Section
                      id="thirdparty"
                      number="11"
                      title="Tautan Ke Situs Lain"
                    >

                      <div className="space-y-5">

                        <p>
                          Layanan ini dapat berisi tautan ke situs lain. Jika
                          Anda mengklik tautan pihak ketiga, Anda akan diarahkan
                          ke situs tersebut.
                        </p>

                        <p>
                          Situs eksternal tersebut tidak dioperasikan oleh kami.
                          Oleh karena itu, kami menyarankan Anda untuk meninjau
                          kebijakan privasi situs web tersebut.
                        </p>

                        <p>
                          Kami tidak memiliki kendali atas dan tidak bertanggung
                          jawab atas konten, kebijakan privasi atau praktik situs
                          atau layanan pihak ketiga mana pun.
                        </p>


                        <div className="grid sm:grid-cols-2 gap-3">

                          <a
                            href="https://radarpulsa.webreport.info"
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-blue-400/20 transition"
                          >

                            <div className="text-[#3E3274] font-medium">
                              KENZIE KOMUNIKA WebReport
                            </div>

                            <div className="text-xs text-gray-500 mt-1">
                              Official reporting website
                            </div>

                          </a>


                          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">

                            <div className="text-[#3E3274] font-medium">
                              Exlusoft
                            </div>

                            <div className="text-xs text-gray-500 mt-1">
                              Partner service
                            </div>

                          </div>

                        </div>


                        <p>
                          Kami sama sekali tidak mengupload daftar kontak
                          (contact list) Anda ke salah satu situs kami.
                          Izin mengakses daftar kontak pada aplikasi hanya
                          digunakan untuk memudahkan Anda memilih nomor tujuan
                          ketika melakukan transaksi.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        12 - CHILDREN
                    ====================================================== */}
                    <Section
                      id="children"
                      number="12"
                      title="Privasi Anak-anak"
                    >

                      <div className="space-y-5">

                        <p>
                          Layanan ini tidak ditujukan kepada siapa pun yang
                          berusia di bawah 13 tahun. Kami tidak secara sadar
                          mengumpulkan informasi yang dapat diidentifikasi
                          secara pribadi dari anak-anak di bawah 13 tahun.
                        </p>

                        <p>
                          Jika kami mengetahui bahwa seorang anak di bawah
                          13 tahun telah memberikan informasi pribadi kepada
                          kami, kami akan segera mengambil langkah yang
                          diperlukan untuk menghapus informasi tersebut dari
                          server kami.
                        </p>

                        <p>
                          Jika Anda adalah orang tua atau wali dan mengetahui
                          bahwa anak Anda telah memberikan informasi pribadi
                          kepada kami, silakan hubungi kami agar kami dapat
                          melakukan tindakan yang diperlukan.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        13 - ACCESS
                    ====================================================== */}
                    <Section
                      id="access"
                      number="13"
                      title="Akses ke Informasi Pribadi"
                    >

                      <p>
                        Jika informasi pribadi Anda berubah, atau jika Anda
                        tidak lagi menginginkan layanan kami, Anda dapat
                        memperbaiki, memperbarui atau menghapus ketidakakuratan
                        dengan melakukan perubahan pada pengaturan akun atau
                        menghubungi customer service kami.
                      </p>

                    </Section>



                    {/* =====================================================
                        14 - RETENTION
                    ====================================================== */}
                    <Section
                      id="retention"
                      number="14"
                      title="Penyimpanan Data"
                    >

                      <div className="space-y-5">

                        <p>
                          Kami akan menyimpan informasi Anda selama akun Anda
                          aktif atau selama yang diperlukan untuk memberikan
                          Layanan kepada Anda.
                        </p>

                        <p>
                          Jika Anda ingin membatalkan akun atau meminta kami
                          untuk tidak lagi menggunakan informasi Anda, silakan
                          hubungi kami.
                        </p>

                        <p>
                          Kami dapat tetap menyimpan dan menggunakan informasi
                          yang diperlukan untuk memenuhi kewajiban hukum,
                          menyelesaikan perselisihan dan menegakkan kewajiban
                          kami.
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        15 - WITHDRAW
                    ====================================================== */}
                    <Section
                      id="withdraw"
                      number="15"
                      title="Penarikan Persetujuan dan Penghapusan Data Pribadi"
                    >

                      <div className="space-y-5">

                        <p>
                          Anda dapat menarik persetujuan Anda atas pengumpulan,
                          perolehan, penyimpanan, penggunaan, pengolahan,
                          penguasaan, pemindahan, pengiriman, pengungkapan
                          dan/atau perlindungan Data Pribadi sebagaimana diatur
                          dalam Kebijakan Privasi ini sesuai dengan hukum dan
                          peraturan perundang-undangan yang berlaku.
                        </p>

                        <p>
                          Anda memahami dan setuju bahwa penarikan persetujuan
                          dan permintaan penghapusan Data Pribadi dapat
                          mengakibatkan Anda tidak lagi dapat mengakses dan/atau
                          menggunakan Aplikasi.
                        </p>


                        <div className="rounded-xl border border-blue-400/10 bg-blue-400/[0.04] p-5">

                          <div className="text-xs uppercase tracking-wider text-blue-400 mb-2">
                            Cara Menghapus Akun
                          </div>

                          <p>
                            Anda dapat meminta penghapusan data pribadi dengan
                            melakukan permintaan pembatalan atau penghapusan
                            akun yang terdaftar di sistem kami melalui menu
                            <strong className="text-[#3E3274]">
                              {" "}Setting / Pengaturan
                            </strong>,
                            kemudian memilih
                            <strong className="text-[#3E3274]">
                              {" "}Request Penutupan / Hapus Akun
                            </strong>.
                          </p>

                        </div>

                      </div>

                    </Section>



                    {/* =====================================================
                        16 - CHANGES
                    ====================================================== */}
                    <Section
                      id="changes"
                      number="16"
                      title="Perubahan Kebijakan"
                    >

                      <div className="space-y-5">

                        <p>
                          Kami dapat memperbarui kebijakan privasi ini untuk
                          mencerminkan perubahan praktik informasi kami.
                        </p>

                        <p>
                          Jika kami melakukan perubahan material, kami dapat
                          memberitahu Anda melalui email, SMS, informasi via
                          website, aplikasi Android, broadcast instant messaging
                          atau media pemberitahuan lainnya sebelum atau sesudah
                          perubahan berlaku efektif.
                        </p>

                        <p>
                          Kebijakan juga dapat berubah tanpa pemberitahuan jika
                          perubahan bersifat minor, tidak membutuhkan persetujuan
                          lebih lanjut dari pengguna, atau tidak ada pihak yang
                          berpotensi dirugikan atas perubahan kebijakan.
                        </p>

                      </div>

                    </Section>

                    {/* =====================================================
                        17 - BLOCK
                    ====================================================== */}
                    <Section
                      id="block"
                      number="17"
                      title="Penangguhan dan Pemblokiran Akun"
                    >

                      <div className="space-y-5">

                        <p>
                          <li>
                            Perusahaan memiliki hak mutlak untuk melakukan tindakan pembatasan layanan, penangguhan (suspend), hingga pemblokiran akun secara sepihak tanpa pemberitahuan sebelumnya apabila:
                          </li>
                          <li>
                            Ditemukan adanya pelanggaran terhadap salah satu, sebagian, atau seluruh isi Syarat dan Ketentuan ini.
                          </li>
                          <li>
                            Terdapat indikasi aktivitas mencurigakan, kecurangan (fraud), manipulasi sistem, atau penggunaan bug pada Aplikasi.
                          </li>
                          <li>
                            Terdeteksi adanya pola transaksi, aliran dana, atau aktivitas akun yang mengindikasikan keterkaitan dengan praktik Judi Online (Judol), Tindak Pidana Pencucian Uang (TPPU), atau kejahatan siber lainnya.
                          </li>
                          <li>
                            Terdapat permintaan resmi dari otoritas hukum atau instansi negara yang berwenang.
                          </li>
                        </p>

                      </div>

                    </Section>

                    {/* =====================================================
                        18 - LIABILITY
                    ====================================================== */}
                    <Section
                      id="liability"
                      number="18"
                      title="Pelepasan Tanggung Jawab Terhadap Penyalahgunaan Ilegal (Disclaimer of Liability)"
                    >

                      <div className="space-y-5">

                        <p>
                          <li>
                            Aplikasi KENZIE KOMUNIKA disediakan semata-mata sebagai platform untuk memfasilitasi transaksi jual-beli Produk Digital. PT Putra Jaya Telesindo beserta seluruh jajaran direksi, manajemen, dan karyawannya dibebaskan dari segala bentuk tuntutan hukum, ganti rugi, pidana, maupun perdata atas segala aktivitas ilegal yang dilakukan oleh Pengguna.
                          </li>
                          <li>
                            Segala bentuk kesalahan, kelalaian, kesengajaan, maupun penyalahgunaan Aplikasi oleh Pengguna untuk tujuan yang melanggar hukum, termasuk namun tidak terbatas pada memfasilitasi deposit/transaksi Judi Online, pencucian uang, pendanaan terorisme, atau penipuan, adalah murni tanggung jawab pribadi Pengguna sepenuhnya.
                          </li>
                          <li>
                            Perusahaan tidak memiliki kewajiban untuk memantau niat, tujuan akhir, atau afiliasi dari setiap Transaksi yang dilakukan Pengguna di luar ekosistem sistem Aplikasi KENZIE KOMUNIKA.
                          </li>
                        </p>

                      </div>

                    </Section>



                    {/* =====================================================
                        CONTACT
                    ====================================================== */}
                    <div className="rounded-2xl border border-blue-400/10 bg-gradient-to-br from-blue-400/[0.06] to-transparent p-6 sm:p-8">

                      <div className="flex flex-col sm:flex-row gap-5 sm:items-center">

                        <div className="w-12 h-12 rounded-xl bg-blue-400/10 border border-blue-400/20 flex items-center justify-center flex-shrink-0">

                          <FcPrivacy/>

                        </div>


                        <div className="flex-1">

                          <h2 className="text-xl font-bold text-[#3E3274]">
                            Punya pertanyaan mengenai privasi?
                          </h2>

                          <p className="mt-1 text-sm text-gray-500">
                            Silakan hubungi kami apabila Anda memiliki
                            pertanyaan atau membutuhkan informasi lebih lanjut
                            mengenai Kebijakan Privasi ini.
                          </p>

                        </div>

                      </div>

                    </div>


                  </div>

                </main>

              </div>
              
            </div>
            
          </div>
      </section>

      {/* =====================================================
          MOBILE STICKY DOCUMENT SWITCHER
      ====================================================== */}

      <div
        className="
          md:hidden
          fixed
          bottom-0
          left-0
          right-0
          z-[9999]
          w-full
          bg-white
          border-t
          border-gray-200
          shadow-[0_-4px_20px_rgba(0,0,0,0.12)]
        "
      >

        {/* =================================================
            TERMS AKTIF
            → TAMPILKAN HANYA PRIVACY
        ================================================== */}

        {activeDocument === "terms" && (

          <button
            type="button"
            onClick={() => handleDocumentChange("privacy")}
            className="
              w-full
              min-h-[64px]
              px-5
              flex
              items-center
              justify-between
              text-left
              text-gray-600
              hover:text-blue-600
              transition
            "
          >

            <span className="text-[16px] font-semibold">
              Kebijakan Privasi
            </span>

            <svg
              className="
                w-6
                h-6
                text-[#3E3274]
              "
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m6 9 6 6 6-6"
              />
            </svg>

          </button>

        )}


        {/* =================================================
            PRIVACY AKTIF
            → TAMPILKAN HANYA TERMS
        ================================================== */}

        {activeDocument === "privacy" && (

          <button
            type="button"
            onClick={() => handleDocumentChange("terms")}
            className="
              w-full
              min-h-[64px]
              px-5
              flex
              items-center
              justify-between
              text-left
              text-gray-600
              hover:text-blue-600
              transition
            "
          >

            <span className="text-[16px] font-semibold">
              Syarat & Ketentuan
            </span>

            <svg
              className="
                w-6
                h-6
                text-[#3E3274]
              "
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m6 9 6 6 6-6"
              />
            </svg>

          </button>

        )}

      </div>
    </div>
  );
}
